import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Modal,
  Box,
  Card,
  Grid,
} from "@mui/material";
import { Link } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";

import Lightbox from "react-awesome-lightbox";
import "react-awesome-lightbox/build/style.css"; // Include the Lightbox styles
import * as settings from "../settings";

const NavigationBar = ({ siteName }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [comVisible, setComVisible] = useState(false);
  const [industriesModalOpen, setIndustriesModalOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setComVisible(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const toggleDrawer = (open) => (event) => {
    setDrawerOpen(open);
  };

  const handleImageClick = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography
            variant="h6"
            component={Link}
            to="/"
            style={{
              flexGrow: 1,
              textDecoration: "none",
              color: "inherit",
              display: "flex",
              alignItems: "center",
            }}
          >
            {siteName}
            <span
              style={{
                fontSize: "0.5em",
                verticalAlign: "top",
                marginLeft: "0.2em",
                opacity: comVisible ? 1 : 0,
                transform: comVisible ? "translateY(0)" : "translateY(-10px)",
                transition: "opacity 1s ease-in, transform 1s ease-in",
              }}
            >
              .com
            </span>
          </Typography>

          {/* Desktop Buttons */}
          <div className="desktop-nav">
            <Button color="inherit" component={Link} to="/api-explorer">
              <b>API Explorer</b>
            </Button>
            <Button color="inherit" component={Link} to="/projects">
              <b>Projects</b>
            </Button>
            <Button color="inherit" component={Link} to="/videos">
              <b>Videos</b>
            </Button>

            <Button
              onClick={() => setIndustriesModalOpen(true)}
              color="inherit"
            >
              <b>Industries</b>
            </Button>
            <Button color="inherit" component={Link} to="/about">
              <b>About Me</b>
            </Button>
            <Button color="inherit" component={Link} to="/contact">
              <b>Contact</b>
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="mobile-nav">
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={toggleDrawer(false)}
            >
              <List style={{ width: "250px", padding: "16px" }}>
                <ListItem
                  button
                  component={Link}
                  to="/api-explorer"
                  onClick={toggleDrawer(false)}
                >
                  <ListItemText primary="API Explorer" />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to="/projects"
                  onClick={toggleDrawer(false)}
                >
                  <ListItemText primary="Projects" />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to="/videos"
                  onClick={toggleDrawer(false)}
                >
                  <ListItemText primary="Videos" />
                </ListItem>

                <ListItem
                  button
                  component={Link}
                  onClick={() => setIndustriesModalOpen(true)}
                >
                  <ListItemText primary="Industries" />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to="/about"
                  onClick={toggleDrawer(false)}
                >
                  <ListItemText primary="About Me" />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to="/contact"
                  onClick={toggleDrawer(false)}
                >
                  <ListItemText primary="Contact" />
                </ListItem>
              </List>
            </Drawer>
          </div>
        </Toolbar>

        <style>
          {`
            .desktop-nav {
              display: none;
            }
            .mobile-nav {
              display: block;
            }

            @media (min-width: 600px) {
              .desktop-nav {
                display: flex;
                gap: 16px;
              }
              .mobile-nav {
                display: none;
              }
            }

            .MuiDrawer-paper {
              width: 250px;
              background-color: #f4f4f4; 
              color: #333;
            }

            a {
              text-decoration: none;
              color: inherit;
            }

            a:visited {
              color: inherit;
            }

            .MuiListItem-root {
              padding: 12px 16px;
            }
          `}
        </style>
      </AppBar>

      <Modal
        open={industriesModalOpen}
        onClose={() => setIndustriesModalOpen(false)}
        aria-labelledby="industries-modal-title"
        aria-describedby="industries-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: "80%" },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 1,
            overflowY: "auto", // Enable scrolling
            maxHeight: "75%",
          }}
        >
          <Typography variant="h4" sx={{ mb: 2 }} gutterBottom>
            Industry Use Cases
          </Typography>

          <Typography sx={{ mb: 2 }}>
            I design and build <b>custom business systems</b> that automate workflows,
            integrate tools, and improve operational efficiency across a wide range of industries.
          </Typography>

          <Typography sx={{ mb: 2 }}>
            Whether starting from an existing system or building from scratch, I translate
            business requirements into <b>scalable, reliable solutions</b> that streamline
            operations and reduce manual work.
          </Typography>

          <Typography sx={{ mb: 3 }}>
            Each solution is built end-to-end, with a focus on <b>system integration, data flow,
              and long-term maintainability</b>, ensuring it continues to support your business as it grows.
          </Typography>
          {/* Grid with Cards */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  MVP Development
                </Typography>
                <Typography variant="body2">
                  Design and build production-ready MVPs to validate ideas quickly and
                  efficiently. Whether starting from scratch or improving an existing
                  product, systems are built for rapid iteration, real-world usage, and
                  long-term scalability. Focus is placed on delivering core functionality,
                  gathering feedback, and evolving the product based on actual user behavior.
                </Typography>

                <img
                  src={`${settings.BASEURL}/sampleSiteScreenshots/mvp.png`}
                  alt="MVP"
                  style={{
                    width: "100%",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                  onClick={() =>
                    handleImageClick(
                      [`${settings.BASEURL}/sampleSiteScreenshots/mvp.png`],
                      0
                    )
                  }
                />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Communications
                </Typography>
                <Typography variant="body2">
                  Design and implement unified communication systems across phone, SMS,
                  email, and other channels. Automate inbound and outbound workflows,
                  notifications, and customer interactions while integrating seamlessly
                  with existing business systems. Solutions can include call routing,
                  conversational messaging, alerts, and real-time communication pipelines
                  built for reliability and scale.
                </Typography>

                <img
                  src={`${settings.BASEURL}/sampleSiteScreenshots/telecommunications.png`}
                  alt="Communications"
                  style={{
                    width: "100%",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                  onClick={() =>
                    handleImageClick(
                      [`${settings.BASEURL}/sampleSiteScreenshots/telecommunications.png`],
                      0
                    )
                  }
                />
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Manufacturing
                </Typography>
                <Typography variant="body2">
                  Build systems that connect shop floor operations with business systems,
                  improving visibility, coordination, and efficiency. Solutions can include
                  inventory tracking, production workflows, order management, and real-time
                  reporting across operations. Integrate equipment data, automate processes,
                  and reduce manual tracking with systems designed for reliability and
                  scalability in production environments.
                </Typography>

                <img
                  src={`${settings.BASEURL}/sampleSiteScreenshots/custom.png`}
                  alt="Manufacturing"
                  style={{
                    width: "100%",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                  onClick={() =>
                    handleImageClick(
                      [`${settings.BASEURL}/sampleSiteScreenshots/custom.png`],
                      0
                    )
                  }
                />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Legal
                </Typography>
                <Typography variant="body2">
                  Build systems for case management, document workflows, and client
                  communication. Automate updates, notifications, and document handling
                  while maintaining strict data integrity and auditability. Solutions can
                  include secure document delivery, e-signature workflows, and AI-assisted
                  document summarization. Systems are designed with a focus on security,
                  access control, and compliance with legal data handling standards.
                </Typography>

                <img
                  src={`${settings.BASEURL}/sampleSiteScreenshots/legal.png`}
                  alt="Legal"
                  style={{
                    width: "100%",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                  onClick={() =>
                    handleImageClick(
                      [`${settings.BASEURL}/sampleSiteScreenshots/legal.png`],
                      0
                    )
                  }
                />
              </Card>
            </Grid>


            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Education
                </Typography>
                <Typography variant="body2">
                  Build systems for course delivery, student onboarding, and operational
                  management. Automate enrollment, communication, and administrative
                  workflows while supporting remote learning and testing environments.
                  Solutions can include learning platforms, faculty management tools,
                  and multi-channel notification systems for events, updates, and outreach.
                </Typography>

                <img
                  src={`${settings.BASEURL}/sampleSiteScreenshots/education.png`}
                  alt="Education"
                  style={{
                    width: "100%",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                  onClick={() =>
                    handleImageClick(
                      [`${settings.BASEURL}/sampleSiteScreenshots/education.png`],
                      0
                    )
                  }
                />
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Real Estate
                </Typography>
                <Typography variant="body2">
                  Design systems to manage lead flow, client communication, and property
                  operations. Automate outreach, inbound inquiries, and follow-up workflows
                  while supporting listings, tenant management, and vendor coordination.
                  Integrate tools for document handling, virtual tours, and transaction
                  workflows to streamline the full lifecycle from lead to close.
                </Typography>

                <img
                  src={`${settings.BASEURL}/sampleSiteScreenshots/realEstateMain.png`}
                  alt="Real Estate"
                  style={{
                    width: "100%",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                  onClick={() =>
                    handleImageClick(
                      [`${settings.BASEURL}/sampleSiteScreenshots/realEstateMain.png`],
                      0
                    )
                  }
                />
              </Card>
            </Grid>

          </Grid>{" "}
        </Box>
      </Modal>

      {lightboxOpen && (
        <Lightbox
          images={lightboxImages.map((img) => ({ url: img }))}
          startIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
};

export default NavigationBar;
